/**
 * AnalysisStackedView - Displays selected measurements as stacked mini-charts.
 *
 * Each selected parameter gets its own mini line chart, stacked vertically.
 * Shows parameter name, unit, and latest value in each chart header, and the
 * tank's target range as a green band when one is configured.
 */
import { useMemo } from 'react';
import { Box, Paper, Text, Stack, Group, Alert, ActionIcon, Tooltip as MantineTooltip } from '@mantine/core';
import { IconAlertCircle, IconNote } from '@tabler/icons-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceArea,
} from 'recharts';
import { format } from 'date-fns';
import {
  TooltipContainer,
  TooltipHeader,
  TooltipRow,
  TooltipNote,
  formatMeasurementValue,
  getDeviceDisplayName,
} from './components/ChartTooltip';
import type { MeasurementPoint, DosingPoint, TaskEvent } from '@/hooks/useAnalysisData';
import type { TankTargetRange } from '@/hooks/useTargetRanges';
import type { ChartAnnotation } from '@/types/chartAnnotation';
import { AnalysisEmptyState } from './AnalysisEmptyState';
import { AnalysisDosingChart } from './AnalysisDosingChart';
import { AnalysisEventsRow } from './AnalysisEventsRow';
import { AnalysisEventLines } from './AnalysisEventLines';
import {
  ChartAnnotationLayer,
  filterAnnotationsForChart,
} from '@/components/charts/ChartAnnotationLayer';
import {
  CHART_COLORS,
  getParameterConfig,
  parseParameterKey,
  formatParameterLabel,
  SPARSE_DATA_SOURCES,
} from './constants';
import {
  CHART_CROSSHAIR_COLOR,
  CHART_FALLBACK_COLOR,
  TARGET_RANGE_BAND_COLOR,
} from '@/utils/chartColors';
import { CHART_GRID_STROKE, CHART_AXIS_TICK } from '@/utils/chartStyles';
import { computeCenteredMovingAverage } from './utils/movingAverage';
import { resolveTargetBand } from './utils/targetBand';

/** Height of each mini-chart in pixels */
const CHART_HEIGHT = 150;

interface AnalysisStackedViewProps {
  /** List of selected parameter keys to display */
  selectedParameters: string[];
  /** Measurement data grouped by parameter key */
  measurements: Record<string, MeasurementPoint[]>;
  /** List of selected dosing chemical names to display */
  selectedDosing?: string[];
  /** Dosing data grouped by chemical name */
  dosing?: Record<string, DosingPoint[]>;
  /** List of selected event type names to display */
  selectedEvents?: string[];
  /** All available maintenance events */
  events?: TaskEvent[];
  /** Date range for event positioning (YYYY-MM-DD format) */
  dateRange?: {
    start: string;
    end: string;
  };
  /** Whether to show vertical event lines on charts */
  showEventLines?: boolean;
  /** Moving-average window length in ms (null = no smoothing). */
  smoothingWindowMs?: number | null;
  /** Tank target ranges; drawn as a band on each chart whose parameter has one. */
  targetRanges?: TankTargetRange[];
  /** Chart annotations to display */
  annotations?: ChartAnnotation[];
  /** Callback when "Add Note" button is clicked */
  onAddNote?: (parameter: string, parameterLabel: string, sources?: string[]) => void;
  /** Callback when an annotation is clicked */
  onAnnotationClick?: (annotation: ChartAnnotation) => void;
}

interface ChartDataPoint {
  timestamp: number;
  value: number;
  device: string;
  note?: string;
  /** Centered moving-average value at this timestamp (present only when smoothing is on). */
  smoothed?: number;
}


/**
 * Transform measurement points to chart data format.
 */
function transformToChartData(points: MeasurementPoint[]): ChartDataPoint[] {
  return points
    .map((point) => ({
      timestamp: new Date(point.timestamp).getTime(),
      value: point.value,
      device: point.device,
      note: point.note,
    }))
    .sort((a, b) => a.timestamp - b.timestamp);
}

/**
 * Get the latest value from measurement points.
 */
function getLatestValue(points: MeasurementPoint[]): number | null {
  if (!points || points.length === 0) return null;

  // Sort by timestamp descending and get the first (most recent)
  const sorted = [...points].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );
  return sorted[0]?.value ?? null;
}

/**
 * Custom tooltip for mini-charts showing parameter value with device source.
 */
export function MiniChartTooltip({
  active,
  payload,
  label,
  compositeKey,
  color,
}: {
  active?: boolean;
  payload?: Array<{ value: number; dataKey?: string; payload: ChartDataPoint }>;
  label?: number;
  compositeKey: string;
  color: string;
}): JSX.Element | null {
  if (!active || !payload || payload.length === 0 || !label) {
    return null;
  }

  // Parse composite key to get param for config lookup
  const { param } = parseParameterKey(compositeKey);
  const config = getParameterConfig(param);
  // With smoothing on, two lines render and payload[0] may be the `smoothed`
  // entry (which has no device/note). Resolve the raw entry explicitly so the
  // tooltip always shows the true raw measurement and source.
  const rawEntry = payload.find((p) => p.dataKey === 'value') ?? payload[0];
  if (!rawEntry) return null;

  const dataPoint = rawEntry.payload;
  const value = dataPoint.value;
  const device = dataPoint.device;

  return (
    <TooltipContainer>
      <TooltipHeader timestamp={label} showTime />
      <TooltipRow
        color={color}
        label={formatParameterLabel(compositeKey)}
        value={formatMeasurementValue(value, param)}
        unit={config.unit}
        subtitle={`Source: ${getDeviceDisplayName(device)}`}
      />
      {dataPoint.note && <TooltipNote note={dataPoint.note} />}
    </TooltipContainer>
  );
}

/**
 * Single mini-chart for one parameter (or parameter-source combination).
 */
function MiniChart({
  compositeKey,
  data,
  color,
  timeDomain,
  selectedEvents = [],
  events = [],
  dateRange,
  showEventLines = false,
  smoothingWindowMs = null,
  targetRanges,
  annotations,
  onAddNote,
  onAnnotationClick,
}: {
  compositeKey: string;
  data: MeasurementPoint[];
  color: string;
  timeDomain?: { startTs: number; endTs: number };
  selectedEvents?: string[];
  events?: TaskEvent[];
  dateRange?: { start: string; end: string };
  showEventLines?: boolean;
  smoothingWindowMs?: number | null;
  targetRanges?: TankTargetRange[];
  annotations?: ChartAnnotation[];
  onAddNote?: () => void;
  onAnnotationClick?: (annotation: ChartAnnotation) => void;
}): JSX.Element {
  // Parse composite key to get param and source for config lookup
  const { param, source } = parseParameterKey(compositeKey);
  const config = getParameterConfig(param);
  const displayLabel = formatParameterLabel(compositeKey);
  const isSparseSource = source ? SPARSE_DATA_SOURCES.has(source) : false;
  const chartData = useMemo(() => transformToChartData(data), [data]);
  const latestValue = useMemo(() => getLatestValue(data), [data]);
  const targetBand = useMemo(
    () => resolveTargetBand([compositeKey], targetRanges),
    [compositeKey, targetRanges]
  );

  // Centered moving average, sampled at the raw timestamps. Null when smoothing
  // is off or the source is sparse (sparse params are never smoothed).
  const smoothedData = useMemo(() => {
    if (smoothingWindowMs == null || isSparseSource) return null;
    return computeCenteredMovingAverage(
      chartData.map((d) => ({ timestamp: d.timestamp, value: d.value })),
      smoothingWindowMs
    );
  }, [chartData, smoothingWindowMs, isSparseSource]);

  // When smoothing is active, merge the smoothed values onto the raw points
  // under a `smoothed` key so a single data array drives both lines. Otherwise
  // use the raw chart data unchanged.
  const renderData = useMemo<ChartDataPoint[]>(() => {
    if (!smoothedData) return chartData;
    return chartData.map((point, index) => ({
      ...point,
      smoothed: smoothedData[index]?.value,
    }));
  }, [chartData, smoothedData]);

  // Filter annotations for this specific parameter/source
  const filteredAnnotations = useMemo(
    () => filterAnnotationsForChart(annotations || [], param, source ? [source] : undefined),
    [annotations, param, source]
  );

  // Calculate Y-axis domain with padding. The target band is included so it is
  // never clipped off when the data sits entirely inside or outside it.
  const yDomain = useMemo(() => {
    if (chartData.length === 0) return [0, 10] as [number, number];

    const values = chartData.map((d) => d.value);
    if (targetBand) values.push(targetBand.min, targetBand.max);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const padding = (max - min) * 0.1 || 1;

    return [Math.max(0, min - padding), max + padding] as [number, number];
  }, [chartData, targetBand]);

  const hasData = chartData.length > 0;
  const isSmoothed = smoothedData !== null;

  return (
    <Paper p="md" withBorder style={{ overflow: 'hidden' }}>
      {/* Chart header with parameter name, unit, and latest value */}
      <Group justify="space-between" mb="xs" wrap="wrap">
        <Group gap="xs" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
          <Box
            style={{
              width: 12,
              height: 12,
              borderRadius: 2,
              backgroundColor: color,
              flexShrink: 0,
            }}
          />
          <Text fw={600} size="sm" truncate>
            {displayLabel}
            {config.unit && (
              <Text span c="dimmed" size="xs" ml={4}>
                ({config.unit})
              </Text>
            )}
          </Text>
        </Group>
        <Group gap="xs" wrap="nowrap">
          {latestValue !== null && (
            <Text size="sm" fw={500} c="dimmed">
              Latest: {formatMeasurementValue(latestValue, param)}
              {config.unit && ` ${config.unit}`}
            </Text>
          )}
          {onAddNote && (
            <MantineTooltip label="Add note" withArrow>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                onClick={onAddNote}
                aria-label="Add note to chart"
              >
                <IconNote size={16} />
              </ActionIcon>
            </MantineTooltip>
          )}
        </Group>
      </Group>

      {/* Chart or empty state */}
      {hasData ? (
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <LineChart data={renderData} margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} vertical={false} />
            <XAxis
              dataKey="timestamp"
              type="number"
              domain={timeDomain ? [timeDomain.startTs, timeDomain.endTs] : ['dataMin', 'dataMax']}
              tickFormatter={(value) => format(new Date(value), 'MMM d')}
              tick={CHART_AXIS_TICK}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              domain={yDomain}
              tick={CHART_AXIS_TICK}
              width={50}
              axisLine={false}
              tickLine={false}
              tickFormatter={(value) => formatMeasurementValue(value, param)}
            />
            <Tooltip
              content={
                <MiniChartTooltip
                  compositeKey={compositeKey}
                  color={color}
                />
              }
              cursor={{ stroke: CHART_CROSSHAIR_COLOR, strokeWidth: 1, strokeDasharray: '4 4' }}
            />
            {/* Target range band, rendered before the lines so it sits behind them */}
            {targetBand && (
              <ReferenceArea
                y1={targetBand.min}
                y2={targetBand.max}
                fill={TARGET_RANGE_BAND_COLOR}
                fillOpacity={0.08}
                stroke={TARGET_RANGE_BAND_COLOR}
                strokeOpacity={0.2}
                strokeDasharray="3 3"
              />
            )}
            {/* Raw measurement line. When smoothing is active it becomes a thin,
                faded backdrop behind the bold smoothed line; otherwise it is the
                primary line (unchanged behaviour). */}
            <Line
              type="monotone"
              dataKey="value"
              stroke={color}
              strokeWidth={isSmoothed ? 1 : 2}
              strokeOpacity={isSmoothed ? 0.3 : undefined}
              dot={isSmoothed
                ? false
                : isSparseSource
                  ? { r: 3, fill: color, stroke: '#fff', strokeWidth: 1.5 }
                  : { r: 2, fill: color }
              }
              activeDot={isSmoothed ? false : { r: isSparseSource ? 5 : 4 }}
              connectNulls
            />
            {/* Bold centered moving-average line (dense sources only when smoothing on). */}
            {isSmoothed && (
              <Line
                type="monotone"
                dataKey="smoothed"
                stroke={color}
                strokeWidth={2}
                dot={false}
                legendType="none"
                connectNulls
              />
            )}
            {/* Annotation overlays */}
            <ChartAnnotationLayer
              annotations={filteredAnnotations}
              onAnnotationClick={onAnnotationClick}
            />
            {/* Event lines using shared component */}
            {dateRange && (
              <AnalysisEventLines
                selectedEvents={selectedEvents}
                events={events}
                dateRange={dateRange}
                showEventLines={showEventLines}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <Alert
          icon={<IconAlertCircle size={14} />}
          color="gray"
          variant="light"
          py="xs"
        >
          No data available for this parameter
        </Alert>
      )}
    </Paper>
  );
}

/**
 * Stacked view showing mini-charts for each selected parameter.
 * Also displays dosing chart and events row below measurements when items are selected.
 */
export function AnalysisStackedView({
  selectedParameters,
  measurements,
  selectedDosing = [],
  dosing = {},
  selectedEvents = [],
  events = [],
  dateRange,
  showEventLines = false,
  smoothingWindowMs = null,
  targetRanges,
  annotations,
  onAddNote,
  onAnnotationClick,
}: AnalysisStackedViewProps): JSX.Element {
  // Compute time domain from date range for chart alignment
  const timeDomain = useMemo(() => {
    if (!dateRange) return undefined;
    return {
      startTs: new Date(`${dateRange.start}T00:00:00`).getTime(),
      endTs: new Date(`${dateRange.end}T23:59:59.999`).getTime(),
    };
  }, [dateRange]);

  // Show empty state when no parameters, dosing, or events selected
  if (selectedParameters.length === 0 && selectedDosing.length === 0 && selectedEvents.length === 0) {
    return <AnalysisEmptyState />;
  }

  return (
    <Stack gap="md">
      {selectedParameters.map((compositeKey, index) => {
        const { param, source } = parseParameterKey(compositeKey);
        return (
          <MiniChart
            key={compositeKey}
            compositeKey={compositeKey}
            data={measurements[compositeKey] || []}
            color={CHART_COLORS[index % CHART_COLORS.length] ?? CHART_FALLBACK_COLOR}
            timeDomain={timeDomain}
            selectedEvents={selectedEvents}
            events={events}
            dateRange={dateRange}
            showEventLines={showEventLines}
            smoothingWindowMs={smoothingWindowMs}
            targetRanges={targetRanges}
            annotations={annotations}
            onAddNote={onAddNote ? () => onAddNote(param, formatParameterLabel(compositeKey), source ? [source] : undefined) : undefined}
            onAnnotationClick={onAnnotationClick}
          />
        );
      })}
      {/* Dosing chart shown below measurement charts when dosing items are selected */}
      <AnalysisDosingChart
        selectedDosing={selectedDosing}
        dosing={dosing}
        timeDomain={timeDomain}
        selectedEvents={selectedEvents}
        events={events}
        dateRange={dateRange}
        showEventLines={showEventLines}
      />
      {/* Events row shown below dosing chart when events are selected */}
      {dateRange && (
        <AnalysisEventsRow
          selectedEvents={selectedEvents}
          events={events}
          dateRange={dateRange}
        />
      )}
    </Stack>
  );
}
